import { ref, computed, onMounted, onUpdated, onBeforeUnmount, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

import { PortfolioService } from '@/services/portfolio.service';
import { ViewportService } from '@/services/viewport.service';
import { cacheService } from '@/services/cache.service';
import { pegService } from '@/services/peg.service';
import { APP_CONSTANTS } from '@/constants/portfolio.constants';
import { FILTER_CONFIG } from '@/constants/filter.constants';

export function usePegComponent(props) {
  const store = useStore();
  const router = useRouter();
  
  const hovered = ref(false);
  const globally_active = ref(false);
  const is_gallery_full_screen = ref(false);
  const image_error = ref(false);
  const peg_ref = ref(null);
  const is_desktop = ref(
    typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
  );

  const peg_id = computed(() => `peg-${props.id}-${props.x}-${props.y}`);

  const active_layer = computed(() => store.getters.activeLayer);
  const active_portfolio = computed(() => store.getters['portfolios/activePortfolio']);
  const active_value_drivers = computed(() => store.getters['valueDrivers/activeValueDrivers']);

  const portfolio_config = computed(() => 
    PortfolioService.getConfig(props.portfolio)
  );

  const portfolio_classes = computed(() => 
    PortfolioService.getClasses(props.portfolio)
  );

  const portfolio_icon = computed(() =>
    PortfolioService.getIcon(super_marker_icon_key.value)
  );

  const thumbnail_src = computed(() => 
    PortfolioService.getThumbnailSrc(props.thumbnail, image_error.value)
  );

  const hasRealThumbnail = computed(() => 
    Boolean(props.thumbnail && props.thumbnail !== 'all' && !image_error.value)
  );

  const active_portfolio_name = computed(() => 
    active_portfolio.value?.name?.toLowerCase() || ''
  );

  const is_hotspot_visible = computed(() => 
    PortfolioService.isHotspotVisible({
      layer: props.layer,
      activeLayer: active_layer.value,
      portfolio: props.portfolio,
      activePortfolio: active_portfolio.value,
      drivers: props.drivers,
      activeValueDrivers: active_value_drivers.value,
    })
  );

  const is_hotspot_special_portfolio = computed(() => {
    return props.portfolio === 7 || props.portfolio === 8;
  });

  const is_matching_portfolio = computed(() => {
    if (!active_portfolio.value) return false;
    
    if (props.portfolio === 7 && active_portfolio.value.id === 7) return true;
    if (props.portfolio === 8 && active_portfolio.value.id === 8) return true;
    
    return false;
  });

  const is_process_layer = computed(() => {
    const id = active_layer.value?.id;
    return id === FILTER_CONFIG.FACILITY_ZONES_LAYER_ID;
  });

  const is_peg_process_layer = computed(() => {
    const layer = props.layer;
    return layer === FILTER_CONFIG.FACILITY_ZONES_LAYER_ID;
  });

  const shouldShowSuperMarker = computed(() => {
    const is_solutions_layer = active_layer.value?.id === APP_CONSTANTS.SOLUTIONS_LAYER;

    if (is_solutions_layer && is_hotspot_special_portfolio.value) {
      return is_matching_portfolio.value || globally_active.value;
    }

    if (is_process_layer.value && is_peg_process_layer.value) {
      return is_desktop.value || globally_active.value;
    }

    return false;
  });

  const is_process_super_marker = computed(() =>
    is_process_layer.value &&
    is_peg_process_layer.value &&
    (is_desktop.value || globally_active.value)
  );

  const shouldShowLabel = computed(() => {
    const is_special_portfolio = PortfolioService.isSpecialPortfolio(active_portfolio_name.value);
    return !is_special_portfolio && globally_active.value && !is_process_super_marker.value;
  });

  const super_marker_icon_key = computed(() =>
    is_process_layer.value && is_peg_process_layer.value ? 'process' : active_portfolio_name.value
  );

  const show_portfolio_icon = computed(() =>
    super_marker_icon_key.value !== 'process'
  );

  const bounding_rect = computed(() => {
    if (!peg_ref.value) return new DOMRect();
    
    const cache_key = `peg-rect-${peg_id.value}`;
    return cacheService.get(cache_key, () => 
      peg_ref.value.getBoundingClientRect()
    );
  });

  const calculated_position = computed(() => 
    ViewportService.calculatePosition(
      props.x,
      props.y,
      bounding_rect.value,
      props.viewport_width,
      props.viewport_height,
      props.show
    )
  );

  const animation_delay = computed(() => 
    ViewportService.calculateAnimationDelay(
      bounding_rect.value,
      props.viewport_width,
      props.show
    )
  );

  const peg_style = computed(() => 
    ViewportService.getPositionStyles(
      calculated_position.value.x,
      calculated_position.value.y,
      animation_delay.value
    )
  );

  const handleImageError = (event) => {
    console.warn(`Failed to load SuperMarker image: ${event.target.src}`);
    image_error.value = true;
  };

  const openModal = () => {
    pegService.hideAll();
    
    router.push({
      name: "HotspotModal",
      params: { hotspotSlug: props.slug },
    });
  };

  const clearCache = () => {
    const cache_key = `peg-rect-${peg_id.value}`;
    cacheService.delete(cache_key);
  };

  const card_hovered = ref(false);

  const handleMouseEnter = async () => {
    hovered.value = true;
    card_hovered.value = true;
    
    await nextTick();
    
    pegService.setActiveHotspot(peg_id.value, true);
  };

  const handleMouseLeave = () => {
    hovered.value = false;
    checkCardHoverState();
    pegService.setActiveHotspot(peg_id.value, false);
  };

  const handleCardHover = () => {
    card_hovered.value = true;
  };

  const handleCardLeave = () => {
    checkCardHoverState();
  };

  const checkCardHoverState = () => {
    if (!hovered.value) {
      setTimeout(() => {
        if (!hovered.value) {
          card_hovered.value = false;
        }
      }, 100);
    }
  };

  let unsubscribe = null;

  const setupGlobalState = () => {
    unsubscribe = pegService.subscribe(peg_id.value, (isActive) => {
      globally_active.value = isActive;
    });
  };

  const cleanupGlobalState = () => {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
  };

  let mediaQueryList = null;
  let mediaQueryCleanup = null;

  onMounted(() => {
    clearCache();
    setupGlobalState();
    if (typeof window !== 'undefined') {
      mediaQueryList = window.matchMedia('(min-width: 768px)');
      is_desktop.value = mediaQueryList.matches;
      const setDesktop = (e) => { is_desktop.value = e.matches; };
      mediaQueryList.addEventListener('change', setDesktop);
      mediaQueryCleanup = () => mediaQueryList?.removeEventListener('change', setDesktop);
    }
  });

  onUpdated(() => {
    clearCache();
  });

  onBeforeUnmount(() => {
    cleanupGlobalState();
    mediaQueryCleanup?.();
  });

  watch(() => props.thumbnail, () => {
    image_error.value = false;
  });

  watch(() => router.currentRoute.value, () => {
    pegService.hideAll();
  });

  return {
    hovered,
    globally_active,
    is_gallery_full_screen,
    image_error,
    peg_ref,
    peg_id,
    portfolio_config,
    portfolio_classes,
    portfolio_icon,
    show_portfolio_icon,
    thumbnail_src,
    hasRealThumbnail,
    active_portfolio_name,
    is_hotspot_visible,
    shouldShowSuperMarker,
    shouldShowLabel,
    peg_style,
    handleImageError,
    openModal,
    clearCache,
    handleMouseEnter,
    handleMouseLeave,
    card_hovered,
    handleCardHover,
    handleCardLeave,
  };
}